import { EventType, IncrementalSource, type eventWithTime } from '@rrweb/types';
import { templatePath } from './ux-url';

/** Attributes that survive; everything else becomes '***'. Values of href/src/style are reduced, not kept. */
const KEEP_ATTR = new Set([
  'class',
  'id',
  'type',
  'role',
  'disabled',
  'checked',
  'selected',
  'tabindex',
  'rel',
  'target',
  'colspan',
  'rowspan',
  'width',
  'height',
]);
const KEEP_ATTR_PREFIX = ['data-ux-', 'data-testid', 'mat-', 'rr_', 'aria-'];
// aria-* is kept EXCEPT these — they routinely carry customer text verbatim (the visible label,
// a placeholder, a spoken-value override, a localized role name).
const MASK_ATTR_EXACT = new Set([
  'aria-label',
  'aria-description',
  'aria-valuetext',
  'aria-placeholder',
  'aria-roledescription',
]);

const URL_TOKEN = /url\([^)]*\)/g;

/** Removes url(...) tokens from a CSS text blob, keeping the rest of the declaration intact. */
function reduceUrls(css: string): string {
  return css.replace(URL_TOKEN, 'none');
}

/** A mutation's `style`/`_cssText` diff can arrive as an object (styleOMValue) instead of a string
 *  when rrweb finds it cheaper to express per-property. Replace any string entry containing a
 *  url() call with 'none' wholesale; leave `false`/[priority,value] tuples untouched. */
function reduceStyleDiffUrls(diff: Record<string, unknown>): void {
  for (const key of Object.keys(diff)) {
    const value = diff[key];
    if (typeof value === 'string' && value.includes('url(')) diff[key] = 'none';
  }
}

/** Recursively reduces url(...) tokens inside any string found in a StyleSheetRule /
 *  StyleDeclaration / AdoptedStyleSheet payload (raw CSS rule text, nested add/remove lists) —
 *  those three don't share a shape worth a dedicated walker like scrubNode's. */
function reduceUrlsDeep(value: unknown): void {
  if (Array.isArray(value)) {
    value.forEach((v, i) => {
      if (typeof v === 'string') value[i] = reduceUrls(v);
      else if (v !== null && typeof v === 'object') reduceUrlsDeep(v);
    });
  } else if (value !== null && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      const v = obj[key];
      if (typeof v === 'string') obj[key] = reduceUrls(v);
      else if (v !== null && typeof v === 'object') reduceUrlsDeep(v);
    }
  }
}

/** A path segment that contains a '.' is (in this app) always a filename, not a route segment —
 *  document/upload hrefs carry the original filename as their last segment. Mask it. */
function maskFileSegments(path: string): string {
  return path
    .split('/')
    .map((seg) => (seg.includes('.') ? '{file}' : seg))
    .join('/');
}

function scrubAttributes(attrs: Record<string, unknown>): void {
  for (const name of Object.keys(attrs)) {
    const value = attrs[name];
    // rrweb inlines every stylesheet into `_cssText` (and a mutation's style diff may arrive as an
    // object) — both MUST be kept, or every replay renders unstyled; only url() calls are reduced.
    if (name === 'style' || name === '_cssText') {
      if (typeof value === 'string') attrs[name] = reduceUrls(value);
      else if (value !== null && typeof value === 'object') reduceStyleDiffUrls(value as Record<string, unknown>);
      continue;
    }
    if (value === null || typeof value !== 'string') continue; // removals / rrweb numeric markers
    if (name === 'href' || name === 'src') {
      attrs[name] = maskFileSegments(templatePath(value));
      continue;
    }
    if (MASK_ATTR_EXACT.has(name)) {
      attrs[name] = '***';
      continue;
    }
    if (KEEP_ATTR.has(name) || KEEP_ATTR_PREFIX.some((p) => name.startsWith(p))) continue;
    attrs[name] = '***';
  }
}

interface SnapshotNode {
  type: number;
  tagName?: string;
  attributes?: Record<string, unknown>;
  childNodes?: SnapshotNode[];
}

function scrubNode(node: SnapshotNode): void {
  if (node.attributes) {
    if (node.tagName === 'link' && String(node.attributes['href'] ?? '').includes('fonts.googleapis.com')) {
      delete node.attributes['href']; // dead link: player falls back to system fonts instead of calling Google
    }
    scrubAttributes(node.attributes);
  }
  node.childNodes?.forEach(scrubNode);
}

/** Runs on EVERY event before it is buffered — the paths rrweb's own masking does not cover. */
export function sanitizeEvent(e: eventWithTime): eventWithTime {
  if (e.type === EventType.Meta) {
    // Route paths never carry a file segment, so no maskFileSegments call is needed here.
    (e.data as { href: string }).href = templatePath((e.data as { href: string }).href);
  } else if (e.type === EventType.FullSnapshot) {
    scrubNode((e.data as unknown as { node: SnapshotNode }).node);
  } else if (e.type === EventType.IncrementalSnapshot) {
    const data = e.data as unknown as {
      source: number;
      adds?: { node: SnapshotNode }[];
      attributes?: { attributes: Record<string, unknown> }[];
      text?: string;
    };
    if (data.source === IncrementalSource.Mutation) {
      data.adds?.forEach((a) => scrubNode(a.node));
      data.attributes?.forEach((a) => scrubAttributes(a.attributes));
    } else if (data.source === IncrementalSource.Input) {
      // rrweb never masks radio/checkbox itself — it emits target.value (and every sibling
      // radio's value) verbatim. isChecked is left alone; it carries no customer data.
      if (typeof data.text === 'string' && data.text.length > 0) data.text = '***';
    } else if (
      data.source === IncrementalSource.StyleSheetRule ||
      data.source === IncrementalSource.StyleDeclaration ||
      data.source === IncrementalSource.AdoptedStyleSheet
    ) {
      reduceUrlsDeep(data);
    }
  }
  return e;
}
